import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import get from 'lodash.get';

// components
import {
    deliveryReturnsTitle,
    deliveryTitle,
    methodNoteFreeShipping,
} from '../messages/pdpDetailsMessages';
import { PdpSharedDetailShipping } from '../PdpSharedDetails/PdpSharedDetailShipping';
import { PdpSharedDetailReturnPolicy } from '../PdpSharedDetails/PdpSharedDetailReturnPolicy';
import { PdpSharedBuyerProtectionBox } from '../PdpSharedBuyerProtectionBox/PdpSharedBuyerProtectionBox';
import { PdpSharedDepositPaymentDetail } from '../PdpSharedDetails/PdpSharedDepositPaymentDetail';
import { PdpRespDetail } from './PdpRespDetail';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid';

// styles
import styles from './PdpRespDetails.scss';

function PdpRespShippingReturnsComponent({
    currency,
    relayVariables,
    viewer,
    item,
    user,
    shipmentQuote,
    shipment,
}) {
    if (!item?.seller?.isAccessible) {
        return null;
    }

    const returnPolicySectionHeader = (
        <span
            dangerouslySetInnerHTML={{
                __html: item?.returnPolicy?.buyerCopy?.returnPolicySectionHeader?.htmlCopy || '',
            }}
        />
    );

    const quoteRequestStatus = get(viewer, 'relevantShipping.quoteRequestStatus');
    const showQuoteDetails = quoteRequestStatus !== 'requestQuote';

    const preLinkQuoteDetails = showQuoteDetails && <Fragment>{methodNoteFreeShipping} </Fragment>;

    return (
        <div
            className={`${styles.wrapper} ${styles.shippingModule}`}
            data-tn="pdp-details-shipping"
        >
            <h2 className={styles.header}>{deliveryReturnsTitle}</h2>
            <GridRow>
                <GridCol desktop={4} mobile={6}>
                    <ul className={styles.shippingReturnsRow}>
                        {/* Shipping */}
                        <PdpRespDetail title={deliveryTitle} dataTn="shipping" printHide>
                            <PdpSharedDetailShipping
                                currency={currency}
                                userId={relayVariables.userId}
                                itemId={relayVariables.itemId}
                                isClient={relayVariables.isClient}
                                item={item}
                                viewer={viewer}
                                user={user}
                                shipmentQuote={shipmentQuote}
                                shipment={shipment}
                                preLinkQuoteDetails={preLinkQuoteDetails}
                                displayInline
                            />
                        </PdpRespDetail>
                        {/* Return Policy */}
                        <PdpRespDetail title={returnPolicySectionHeader} dataTn="return-policy">
                            <PdpSharedDetailReturnPolicy
                                userId={relayVariables.userId}
                                itemId={relayVariables.itemId}
                                item={item}
                            />
                        </PdpRespDetail>
                    </ul>
                </GridCol>
                {item?.isDepositAvailable && (
                    <GridCol desktop={4} mobile={6}>
                        <div className={styles.depositRow}>
                            <PdpSharedDepositPaymentDetail
                                userId={relayVariables.userId}
                                item={item}
                                wrapper={PdpRespDetail}
                                linkClass={styles.depositLink}
                            />
                        </div>
                    </GridCol>
                )}
                <GridCol desktop={4} mobile={12}>
                    <div className={`${styles.printHide} ${styles.buyerProtectionRow}`}>
                        <PdpSharedBuyerProtectionBox user={user} />
                    </div>
                </GridCol>
            </GridRow>
        </div>
    );
}

PdpRespShippingReturnsComponent.propTypes = {
    currency: PropTypes.string,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isTrade: PropTypes.bool,
        isClient: PropTypes.bool,
    }).isRequired,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
};

export const PdpRespShippingReturns = createFragmentContainer(PdpRespShippingReturnsComponent, {
    viewer: graphql`
        fragment PdpRespShippingReturns_viewer on Viewer {
            ...PdpSharedDetailShipping_viewer
            relevantShipping(itemId: $itemId, buyerId: $userId) {
                quoteRequestStatus
            }
        }
    `,
    user: graphql`
        fragment PdpRespShippingReturns_user on User {
            ...PdpSharedDetailShipping_user
            ...PdpSharedBuyerProtectionBox_user
        }
    `,
    item: graphql`
        fragment PdpRespShippingReturns_item on Item {
            ...PdpSharedDetailReturnPolicy_item
            ...PdpSharedDepositPaymentDetail_item
            ...PdpSharedDetailShipping_item
            isDepositAvailable(buyerId: $userId)
            returnPolicy(userId: $userId) {
                buyerCopy {
                    returnPolicySectionHeader {
                        htmlCopy
                    }
                }
            }
            seller {
                isAccessible
            }
        }
    `,
    shipmentQuote: graphql`
        fragment PdpRespShippingReturns_shipmentQuote on ShipmentQuote {
            ...PdpSharedDetailShipping_shipmentQuote
        }
    `,
    shipment: graphql`
        fragment PdpRespShippingReturns_shipment on Shipment {
            ...PdpSharedDetailShipping_shipment
        }
    `,
});
